import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Save, Plus, X, FileText, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

export default function PurchaseReceiptNew() {
  const { products } = useProducts();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/mal-kabul');
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    supplier_id: '', warehouse_id: '', invoice_number: '', invoice_date: '', notes: '', items: []
  });
  // Faturadan aktar
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [showInvoicePicker, setShowInvoicePicker] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    api.get('/suppliers').then(r => setSuppliers(r.data)).catch(() => {});
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
  }, []);

  // Tedarikçi değişince ilgili faturaları çek
  useEffect(() => {
    if (!form.supplier_id) { setSupplierInvoices([]); return; }
    setLoadingInvoices(true);
    api.get(`/invoices?supplier_id=${form.supplier_id}&per_page=50`)
      .then(r => setSupplierInvoices(r.data.invoices || r.data || []))
      .catch(() => {})
      .finally(() => setLoadingInvoices(false));
  }, [form.supplier_id]);

  const importFromInvoice = (inv) => {
    const newItems = (inv.items || [])
      .filter(it => it.matched_product_id)
      .map(it => ({
        product_card_id: it.matched_product_id,
        product_name: it.matched_product_name || it.description,
        ordered_qty: 0,
        received_qty: String(it.quantity || ''),
        unit: it.unit || 'adet',
        unit_price: String(it.unit_price || ''),
      }));
    if (!newItems.length) { toast.error('Faturada eşleşmiş ürün bulunamadı'); return; }
    setForm(p => ({
      ...p,
      invoice_number: inv.invoice_number || p.invoice_number,
      invoice_date: inv.invoice_date || p.invoice_date,
      items: newItems,
    }));
    setShowInvoicePicker(false);
    toast.success(`${newItems.length} kalem aktarıldı`);
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { product_card_id: '', received_qty: '', unit_price: '', unit: 'kg', ordered_qty: 0, product_name: '' }]
    }));
  };

  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i][field] = value;
    if (field === 'product_card_id') {
      const p = products.find(x => x.id === value);
      if (p) {
        items[i].unit = p.unit;
        items[i].product_name = p.name;
        if (p.last_purchase_price) items[i].unit_price = p.last_purchase_price.toString();
      }
    }
    setForm(prev => ({ ...prev, items }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id || !form.warehouse_id || form.items.length === 0) {
      toast.error('Tedarikci, depo ve en az bir urun gerekli');
      return;
    }
    setLoading(true);
    try {
      await api.post('/purchase-receipts', {
        ...form,
        items: form.items.map(i => ({
          product_card_id: i.product_card_id,
          product_name: i.product_name,
          ordered_qty: parseFloat(i.ordered_qty || 0),
          received_qty: parseFloat(i.received_qty),
          unit: i.unit,
          unit_price: parseFloat(i.unit_price),
          // Siparis hangi cevrim carpaniyla verildiyse onunla islensin
          base_unit: i.base_unit,
          base_factor: i.base_factor
        }))
      });
      toast.success('Mal kabul kaydedildi');
      navigate('/erp/purchase-orders');
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
    setLoading(false);
  };

  const total = form.items.reduce((sum, i) => sum + (parseFloat(i.received_qty) || 0) * (parseFloat(i.unit_price) || 0), 0);

  return (
    <div data-testid="purchase-receipt-new-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Mal Kabul</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Fatura Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Tedarikci *</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm(p => ({ ...p, supplier_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="receipt-supplier-select">
                  <SelectValue placeholder="Tedarikci secin" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Depo *</Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm(p => ({ ...p, warehouse_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="receipt-warehouse-select">
                  <SelectValue placeholder="Depo secin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Fatura Numarasi</Label>
              <Input value={form.invoice_number} onChange={(e) => setForm(p => ({ ...p, invoice_number: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="receipt-invoice-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Fatura Tarihi</Label>
              <Input type="date" value={form.invoice_date}
                onChange={(e) => setForm(p => ({ ...p, invoice_date: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="receipt-date-input" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Urunler</CardTitle>
            <div className="flex gap-2 relative">
              {form.supplier_id && (
                <div className="relative">
                  <Button type="button" size="sm" variant="outline"
                    className="border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
                    onClick={() => setShowInvoicePicker(v => !v)}
                    disabled={loadingInvoices}>
                    <FileText size={14} className="mr-1" />
                    {loadingInvoices ? 'Yükleniyor...' : 'Faturadan Aktar'}
                    <ChevronDown size={12} className="ml-1" />
                  </Button>
                  {showInvoicePicker && (
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-[#d0c4b0] rounded-lg shadow-lg min-w-[320px] max-h-72 overflow-y-auto">
                      {supplierInvoices.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-[#8a7e70]">Bu tedarikçiye ait fatura bulunamadı</p>
                      ) : supplierInvoices.map(inv => (
                        <button key={inv.id} type="button"
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#f5f0e8] border-b border-[#e8dfd0] last:border-0"
                          onClick={() => importFromInvoice(inv)}>
                          <span className="font-semibold text-[#1a1916]">{inv.invoice_number || '—'}</span>
                          <span className="text-[#8a7e70] ml-2">{inv.invoice_date}</span>
                          <span className="text-[#3d6e4e] ml-2 font-mono text-xs">{(inv.amount_try || 0).toLocaleString('tr-TR')} TL</span>
                          <span className="text-[#8a7e70] text-xs ml-2">({(inv.items || []).filter(i => i.matched_product_id).length} ürün)</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Button type="button" onClick={addItem} size="sm"
                className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="receipt-add-item">
                <Plus size={14} className="mr-1" /> Urun Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-24">Alinan</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-20">Birim</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-28">Birim Fiyat</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.items.map((item, i) => {
                  const lineTotal = (parseFloat(item.received_qty) || 0) * (parseFloat(item.unit_price) || 0);
                  return (
                    <TableRow key={i} className="border-[#d0c4b0]">
                      <TableCell>
                        <Select value={item.product_card_id} onValueChange={(v) => updateItem(i, 'product_card_id', v)}>
                          <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid={`receipt-item-product-${i}`}>
                            <SelectValue placeholder="Urun secin" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" value={item.received_qty}
                          onChange={(e) => updateItem(i, 'received_qty', e.target.value)}
                          className="bg-white border-[#d0c4b0]" data-testid={`receipt-item-qty-${i}`} />
                      </TableCell>
                      <TableCell className="text-sm text-[#8a7e70]">{item.unit}</TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" value={item.unit_price}
                          onChange={(e) => updateItem(i, 'unit_price', e.target.value)}
                          className="bg-white border-[#d0c4b0]" data-testid={`receipt-item-price-${i}`} />
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-[#1a1916]">{lineTotal.toFixed(2)} TL</TableCell>
                      <TableCell>
                        <Button type="button" variant="ghost" size="icon"
                          onClick={() => setForm(p => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}
                          className="text-[#7a3535] hover:bg-[#7a3535]/10" data-testid={`receipt-remove-${i}`}>
                          <X size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {form.items.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-6 text-[#8a7e70]">Urun ekleyin</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-4">
          <p className="text-sm text-[#8a7e70]">
            Toplam: <span className="font-bold text-[#1a1916] text-lg">{total.toFixed(2)} TL</span>
          </p>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={geri} className="border-[#d0c4b0]" data-testid="cancel-btn">Iptal</Button>
            <Button type="submit" disabled={loading} className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold" data-testid="save-receipt-btn">
              <Save size={16} className="mr-2" /> {loading ? 'Kaydediliyor...' : 'Mal Kabul Kaydet'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
